import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Sparkles, Loader2, X, ExternalLink, Globe2, MapPin, Clock, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* AI Deep-Research dialog for a single inventory item.

   Calls POST /api/admin/ai/research-item with the item's known facts; the
   backend returns a structured vendor list (name, region, url, price, lead
   time, shipping, notes) + summary + recommendation. Admins can toggle the
   provider (GPT-5.2 / Gemini 3 Pro) and re-run for a second opinion.

   Renders a stack of vendor cards in the app's blue/cream palette. Each
   card surfaces the URL as a "Visit vendor" link and lets the admin
   one-click "Use this vendor" — which patches the inventory item with the
   chosen vendor name + reference URL + lead time + shipping mode. */
export default function AiResearchDialog({ open, onClose, item, token, onApply }) {
  const [provider, setProvider] = useState('openai');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const run = async (prov = provider) => {
    setBusy(true); setError(null); setData(null);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/ai/research-item`,
        {
          name: item.name || '',
          brand: item.brand || '',
          vendor: item.vendor || '',
          source_market: item.source_market || '',
          description: item.description || '',
          quantity_requested: item.quantity_requested || '',
          provider: prov,
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 120000 },
      );
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Research failed');
    } finally {
      setBusy(false);
    }
  };

  const apply = async (v) => {
    if (!onApply) return;
    await onApply({
      vendor: v.name,
      vendor_reference: v.url || item.vendor_reference || '',
      lead_time: v.lead_time || item.lead_time || '',
      shipping_method: (v.shipping || '').toLowerCase().includes('air') ? 'air'
                     : (v.shipping || '').toLowerCase().includes('sea') ? 'sea'
                     : (v.shipping || '').toLowerCase().includes('local') ? 'local' : item.shipping_method || '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[96vw] max-w-4xl max-h-[92vh] overflow-y-auto p-0" style={{ backgroundColor: '#FAF8F3' }} data-testid="ai-research-dialog">
        {/* Header */}
        <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', color: 'white' }}>
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1 opacity-80" style={{ fontFamily: 'Manrope' }}>
                <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" /> AI Deep Research
              </p>
              <h2 className="text-2xl sm:text-3xl truncate" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {item.name || 'Untitled item'}
              </h2>
              <p className="text-[11px] mt-0.5 opacity-80" style={{ fontFamily: 'Manrope' }}>
                {[item.brand, item.vendor, item.source_market].filter(Boolean).join(' · ') || 'No brand / vendor / market specified'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
              data-testid="ai-research-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Provider toggle */}
          <div className="flex items-center gap-2 mt-3">
            <ProviderPill active={provider === 'openai'} label="GPT-5.2" onClick={() => setProvider('openai')} />
            <ProviderPill active={provider === 'gemini'} label="Gemini 3 Pro" onClick={() => setProvider('gemini')} />
            <button
              onClick={() => run()}
              disabled={busy}
              className="ml-auto flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[11px] font-bold disabled:opacity-60 transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: 'white', color: '#003DA5', fontFamily: 'Manrope' }}
              data-testid="ai-research-run"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? 'Researching…' : (data ? 'Re-run' : 'Run deep research')}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {!data && !busy && !error && (
            <div className="py-12 text-center">
              <Globe2 className="w-12 h-12 mx-auto mb-3 opacity-30" style={{ color: '#003DA5' }} />
              <p className="text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                Pick a model and press <strong>"Run deep research"</strong> to get a global vendor scan with regional pricing &amp; lead times.
              </p>
            </div>
          )}
          {busy && (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto animate-spin mb-3" style={{ color: '#003DA5' }} />
              <p className="text-sm font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Scanning global vendors…</p>
              <p className="text-[11px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>This typically takes 10–60 seconds.</p>
            </div>
          )}
          {error && (
            <div className="py-6 px-4 rounded-lg" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
              <p className="text-sm font-bold" style={{ color: '#B91C1C', fontFamily: 'Manrope' }}>Research failed</p>
              <p className="text-xs mt-1" style={{ color: '#991B1B', fontFamily: 'Manrope' }}>{error}</p>
            </div>
          )}
          {data && (
            <>
              {data.summary && (
                <div className="mb-5 p-4 rounded-xl" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Executive summary</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{data.summary}</p>
                  {data.recommended && (
                    <p className="text-[12px] mt-2 italic" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                      <strong>Recommended:</strong> {data.recommended}
                    </p>
                  )}
                </div>
              )}
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                Vendor options · {data.vendors?.length || 0}
              </p>
              <div className="space-y-3">
                {(data.vendors || []).map((v, i) => (
                  <div key={i} className="p-4 rounded-xl" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }} data-testid={`ai-vendor-${i}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-bold leading-tight" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
                          {v.name}
                        </p>
                        <div className="flex flex-wrap items-center gap-2.5 mt-1.5 text-[11px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                          {v.region && (<span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.region}</span>)}
                          {v.lead_time && (<span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {v.lead_time}</span>)}
                          {v.shipping && (<span className="flex items-center gap-1"><Package className="w-3 h-3" /> {v.shipping}</span>)}
                        </div>
                        {v.notes && (
                          <p className="text-[12px] mt-2" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{v.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {v.est_price && (
                          <span className="text-[13px] font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                            {String(v.est_price).startsWith('$') ? v.est_price : `$${v.est_price}`}
                          </span>
                        )}
                        <div className="flex gap-1.5">
                          {v.url && (
                            <a href={v.url} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
                              style={{ backgroundColor: '#F0F4FA', color: '#003DA5', fontFamily: 'Manrope' }}
                              data-testid={`ai-vendor-visit-${i}`}
                            >
                              <ExternalLink className="w-3 h-3" /> Visit
                            </a>
                          )}
                          {onApply && (
                            <button
                              onClick={() => apply(v)}
                              className="px-2.5 py-1 rounded-full text-[10px] font-bold text-white"
                              style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                              data-testid={`ai-vendor-apply-${i}`}
                            >
                              Use this vendor
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {data.sources?.length > 0 && (
                <div className="mt-5 pt-4" style={{ borderTop: '1px solid #D9E3F1' }}>
                  <p className="text-[9px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Source notes</p>
                  <ul className="text-[11px] list-disc pl-4" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                    {data.sources.slice(0, 6).map((s, i) => (<li key={i}>{s}</li>))}
                  </ul>
                </div>
              )}
              <p className="text-[9px] mt-4 text-right" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
                Generated by {data.provider} · {new Date(data.generated_at).toLocaleString()}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProviderPill({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1 rounded-full text-[10px] font-bold transition-all"
      style={{
        backgroundColor: active ? 'white' : 'rgba(255,255,255,0.15)',
        color: active ? '#003DA5' : 'white',
        fontFamily: 'Manrope',
      }}
      data-testid={`ai-research-provider-${label.toLowerCase().split(' ')[0]}`}
    >
      {label}
    </button>
  );
}
